import React from 'react';
import { useTranslation } from 'react-i18next';
import CampText from '../CampText';

/**
 * Passa 3 — la línia de temps.
 *
 * **Va de dalt a baix, de l'horitzó cap a avui.** No és una floritura: tot
 * l'exercici consisteix a preguntar-se «què havia de passar just abans perquè
 * això fos possible?», i una línia que avanci cap al futur convida a fer
 * exactament el contrari. Llegir cap avall és anar cap enrere, i per això el
 * botó d'afegir és a baix de tot i diu «afegeix una fita anterior».
 *
 * Vertical i no horitzontal perquè cada fita porta una frase de descripció, que
 * en horitzontal no hi cap.
 *
 * **Aquí també es marquen les crítiques i es puntua el control.** Cap de les
 * dues coses crea res nou —etiqueten fites que ja hi són— i separar-ho en una
 * pantalla a part obligaria a repassar tota la llista un segon cop.
 *
 * **Sense colors.** El control es llegeix amb tres punts plens o buits i la
 * paraula al costat; l'única cosa marcada amb l'accent és el punt de les fites
 * crítiques. Amb un color per nivell allò quedava com un semàfor.
 */

const NIVELLS = [
  { valor: 'full', plens: 3 },
  { valor: 'partial', plens: 2 },
  { valor: 'none', plens: 0 }
];

const Punts = ({ plens }) => (
  <span className="inline-flex gap-0.5" aria-hidden="true">
    {[0, 1, 2].map(i => (
      <span
        key={i}
        className={`block h-1.5 w-1.5 rounded-full border border-current ${i < plens ? 'bg-current' : ''}`}
      />
    ))}
  </span>
);

const TimelineStep = ({ exercise, milestones, onCreate, onPatch, onDelete, onReorder, onPresent }) => {
  const { t } = useTranslation();

  const ordenades = [...milestones].sort((a, b) => (a.position - b.position) || (a.id - b.id));
  const critiques = ordenades.filter(m => m.is_critical).length;

  const mou = (index, direccio) => {
    const desti = index + direccio;
    if (desti < 0 || desti >= ordenades.length) return;
    const ordre = ordenades.map(m => m.id);
    [ordre[index], ordre[desti]] = [ordre[desti], ordre[index]];
    onReorder(ordre);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-neutral-900">
            {t('backcasting.step3Title', { horizon: exercise.horizon_label || t('backcasting.theHorizon') })}
          </h3>
          <p className="mt-1 max-w-3xl text-sm text-neutral-600">{t('backcasting.step3Lead')}</p>
        </div>

        {/* La línia de temps també es pot projectar, però des d'aquí i no des
            de la capçalera: allà hi ha el pla d'acció, que és el resultat de
            l'exercici. Cada cosa es presenta des d'on viu; un sol botó que
            hagués de triar entre les dues obligaria a preguntar-ho cada cop. */}
        {onPresent && ordenades.length > 0 && (
          <button
            type="button"
            onClick={onPresent}
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-md border border-neutral-300 px-3 py-1.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 8V4m0 0h4M4 4l5 5m11-5v4m0-4h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
            </svg>
            {t('backcasting.presentTimeline')}
          </button>
        )}
      </div>

      {/* El futur ancorat, sempre visible a dalt de tot */}
      <div className="flex flex-wrap justify-between gap-3 rounded-lg border border-dashed border-neutral-300 bg-neutral-50 px-4 py-3">
        <p className="max-w-3xl text-sm text-neutral-600">
          <span className="font-semibold text-neutral-900">
            {exercise.horizon_label || t('backcasting.theHorizon')} · {t('backcasting.preferredFuture')}
          </span>
          {exercise.vision_statement ? ` — ${exercise.vision_statement}` : ''}
        </p>
        <span className="text-xs text-neutral-400">{t('backcasting.fromStep1')}</span>
      </div>

      {ordenades.length === 0 ? (
        <p className="rounded-lg border border-dashed border-neutral-300 px-4 py-10 text-center text-sm text-neutral-500">
          {t('backcasting.timelineEmpty')}
        </p>
      ) : (
        <ol className="relative ml-1 space-y-4 border-l border-neutral-200 pl-6">
          {ordenades.map((fita, index) => (
            <li key={fita.id} className="relative">
              <span
                className={`absolute -left-[1.9rem] top-5 block h-2.5 w-2.5 rounded-full border-2 ${
                  fita.is_critical
                    ? 'border-accent-600 bg-accent-600 ring-4 ring-accent-100'
                    : 'border-neutral-300 bg-white'
                }`}
                aria-hidden="true"
              />

              <div className={`rounded-lg border bg-white p-4 ${
                fita.is_critical ? 'border-accent-200' : 'border-neutral-200'
              }`}>
                <div className="flex flex-wrap items-start gap-3">
                  <div className="w-36 flex-shrink-0">
                    <CampText
                      value={fita.period_label}
                      onSave={(v) => onPatch(fita.id, { period_label: v })}
                      placeholder={t('backcasting.periodPlaceholder')}
                      /* Els anys van en monoespaiada: és una columna de xifres
                         i és el que la fa llegible d'una ullada. */
                      inputClassName="text-xs font-mono"
                    />
                  </div>
                  <div className="min-w-[14rem] flex-1">
                    <CampText
                      value={fita.title}
                      onSave={(v) => onPatch(fita.id, { title: v || t('backcasting.untitledMilestone') })}
                      placeholder={t('backcasting.milestoneTitlePlaceholder')}
                      inputClassName="font-medium"
                    />
                  </div>
                  <div className="flex flex-shrink-0 gap-1 pt-0.5">
                    <button type="button" onClick={() => mou(index, -1)} disabled={index === 0}
                      title={t('backcasting.moveLater')} aria-label={t('backcasting.moveLater')}
                      className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 disabled:opacity-30 disabled:hover:bg-transparent">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <button type="button" onClick={() => mou(index, 1)} disabled={index === ordenades.length - 1}
                      title={t('backcasting.moveEarlier')} aria-label={t('backcasting.moveEarlier')}
                      className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700 disabled:opacity-30 disabled:hover:bg-transparent">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <button type="button" onClick={() => onDelete(fita.id)}
                      title={t('common.delete')} aria-label={t('common.delete')}
                      className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-error-dark">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="mt-3">
                  <CampText
                    value={fita.description}
                    onSave={(v) => onPatch(fita.id, { description: v })}
                    placeholder={t('backcasting.milestoneDescriptionPlaceholder')}
                    multiline
                    rows={2}
                    inputClassName="text-sm"
                  />
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onPatch(fita.id, { is_critical: !fita.is_critical })}
                    aria-pressed={!!fita.is_critical}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      fita.is_critical
                        ? 'border-accent-300 bg-accent-50 text-accent-700'
                        : 'border-neutral-200 bg-neutral-50 text-neutral-500 hover:border-neutral-300'
                    }`}
                  >
                    <svg className="h-3 w-3" fill={fita.is_critical ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                    </svg>
                    {t('backcasting.criticalMilestone')}
                  </button>

                  <span className="mx-1 h-4 w-px bg-neutral-200" aria-hidden="true" />

                  <div className="flex flex-wrap gap-1.5" role="group" aria-label={t('backcasting.controlLabel')}>
                    {NIVELLS.map(n => {
                      const actiu = fita.control_level === n.valor;
                      return (
                        <button
                          key={n.valor}
                          type="button"
                          aria-pressed={actiu}
                          onClick={() => onPatch(fita.id, { control_level: actiu ? null : n.valor })}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${
                            actiu
                              ? 'border-neutral-400 bg-neutral-100 font-medium text-neutral-800'
                              : 'border-neutral-200 bg-white text-neutral-500 hover:border-neutral-300'
                          }`}
                        >
                          <Punts plens={n.plens} />
                          {t(`backcasting.control.${n.valor}`)}
                        </button>
                      );
                    })}
                  </div>

                  {fita.is_critical && !fita.control_level && (
                    <span className="text-xs text-neutral-400">{t('backcasting.needsControlScore')}</span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          /* El títol va buit i explícit. La fita neix en blanc a posta —s'escriu
             a la targeta, no en un diàleg previ—, però `title` és obligatori a
             l'esquema, i enviar-hi res feia que el servidor respongués 422 i el
             botó no fes res. */
          onClick={() => onCreate({ title: '', position: ordenades.length })}
          className="rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50"
        >
          {ordenades.length === 0 ? t('backcasting.addFirstMilestone') : t('backcasting.addEarlierMilestone')}
        </button>
        <span className="text-xs text-neutral-500">
          {t('backcasting.timelineCount', { total: ordenades.length, critical: critiques })}
        </span>
      </div>

      <div className="flex flex-wrap justify-between gap-3 rounded-lg border border-dashed border-neutral-300 bg-neutral-50 px-4 py-3">
        <span className="text-sm font-semibold text-neutral-900">{t('backcasting.today')}</span>
        <span className="text-xs text-neutral-400">{t('backcasting.todayNote')}</span>
      </div>
    </div>
  );
};

export default TimelineStep;
